import type { ImageLoaderProps } from 'next/image';

/**
 * Unified loader for:
 * 1) Cloudflare Images (imagedelivery.net) using image IDs and variants
 * 2) Cloudflare Image Resizing for regular asset paths served behind Cloudflare
 *
 * Env:
 * - NEXT_PUBLIC_CF_IMAGES_BASE (default: https://imagedelivery.net)
 * - NEXT_PUBLIC_CF_IMAGES_ACCOUNT_HASH (required for CF Images)
 * - NEXT_PUBLIC_CF_IMAGES_DEFAULT_VARIANT (fallback variant, e.g. "public")
 * - NEXT_PUBLIC_CF_IMAGES_VARIANTS_JSON (optional width→variant mapping JSON)
 * - NEXT_PUBLIC_CLOUDFLARE_IMAGE_HOST (for Image Resizing on your own host)
 *
 * Usage:
 * - Pass `src` as:
 *   a) "cfimg:<imageId>" → uses Cloudflare Images
 *   b) a raw `<imageId>` (UUID-like) → uses Cloudflare Images
 *   c) an absolute imagedelivery URL → returned as-is (optionally variant-swapped)
 *   d) a path like "/images/foo.webp" → served via Image Resizing if host is set
 */
export default function cloudflareImageLoader({ width, quality, src }: ImageLoaderProps): string {
  const q = quality ?? 75;

  // --- Cloudflare Images (imagedelivery.net) ---
  const cfImagesBase = process.env.NEXT_PUBLIC_CF_IMAGES_BASE || 'https://imagedelivery.net';
  const cfAccountHash = process.env.NEXT_PUBLIC_CF_IMAGES_ACCOUNT_HASH || '';
  const defaultVariant = process.env.NEXT_PUBLIC_CF_IMAGES_DEFAULT_VARIANT || 'public';
  const variantsJson = process.env.NEXT_PUBLIC_CF_IMAGES_VARIANTS_JSON || '';
  const flexible = (process.env.NEXT_PUBLIC_CF_IMAGES_FLEXIBLE || '').toLowerCase() === 'true';
  // Upscale requested width to better match high-DPR devices (browser picks from srcset,
  // but scaling here yields crisper results with Flexible Variants).
  const dprScaleRaw = process.env.NEXT_PUBLIC_CF_IMAGES_DPR_SCALE || '1';
  const dprScale = Math.max(1, Math.min(2, Number(dprScaleRaw) || 1));
  const capWidth = (w: number) => Math.min(Math.max(1, Math.round(w)), 3200);
  const sharpenEnv = (process.env.NEXT_PUBLIC_CF_IMAGES_SHARPEN || '').trim();
  const sharpenThresholdEnv = (process.env.NEXT_PUBLIC_CF_IMAGES_SHARPEN_AT_OR_BELOW || '').trim();
  const sharpenValue = (() => {
    if (!sharpenEnv) return 0;
    if (sharpenEnv.toLowerCase() === 'true') return 1;
    const n = Number(sharpenEnv);
    return Number.isFinite(n) && n > 0 ? Math.min(10, Math.max(0, Math.round(n))) : 0;
  })();
  const sharpenThreshold = (() => {
    const n = Number(sharpenThresholdEnv);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  const shouldSharpen = (w: number) => {
    if (sharpenValue <= 0) return false;
    if (sharpenThreshold == null) return true;
    return w <= sharpenThreshold;
  };

  const pickVariant = (w: number): string => {
    if (!variantsJson) return defaultVariant;
    try {
      const map: Record<string, string> = JSON.parse(variantsJson);
      // Find the smallest breakpoint >= width; else use the largest defined
      const breakpoints = Object.keys(map)
        .map((k) => Number(k))
        .filter((n) => !Number.isNaN(n))
        .sort((a, b) => a - b);
      for (const bp of breakpoints) {
        if (w <= bp) return map[String(bp)];
      }
      return map[String(breakpoints[breakpoints.length - 1])] || defaultVariant;
    } catch {
      return defaultVariant;
    }
  };

  const isUUIDish = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(src);
  const isPrefixedId = src.startsWith('cfimg:');
  const isImageDeliveryUrl = /^https?:\/\/[^/]*imagedelivery\.net\//i.test(src);

  if (isPrefixedId || isUUIDish) {
    const imageId = isPrefixedId ? src.slice('cfimg:'.length) : src;
    if (!cfAccountHash) {
      // Without account hash we cannot construct CF Images URL; fall back to original
      return src;
    }
    if (flexible) {
      // Flexible Variants: encode transformations in the path segment
      // Docs: https://developers.cloudflare.com/images/manage-images/enable-flexible-variants/
      // Keep it simple: only width + format auto; Cloudflare will handle DPR/srcset choices.
      // Never upscale above requested width; only allow mild DPR scaling when configured
      const effW = Math.min(capWidth(width * dprScale), width);
      const sharpenSeg = shouldSharpen(effW) ? `,sharpen=${sharpenValue}` : '';
      // Single quality parameter for deterministic SSR/CSR output
      return `${cfImagesBase.replace(/\/$/, '')}/${cfAccountHash}/${imageId}/w=${effW},quality=${q},format=auto${sharpenSeg}`;
    }
    const variant = pickVariant(width);
    return `${cfImagesBase.replace(/\/$/, '')}/${cfAccountHash}/${imageId}/${variant}`;
  }

  if (isImageDeliveryUrl) {
    // Optionally replace the variant based on width if variants mapping exists
    if (flexible) {
      try {
        const url = new URL(src);
        const parts = url.pathname.split('/').filter(Boolean);
        // Expect: /<hash>/<id>/<variant or params>
        if (parts.length >= 3) {
          // Replace last segment with flexible params
          const imageId = parts[1];
          const effW = Math.min(capWidth(width * dprScale), width);
          const sharpenSeg = shouldSharpen(effW) ? `,sharpen=${sharpenValue}` : '';
          return `${url.origin}/${parts[0]}/${imageId}/w=${effW},quality=${q},format=auto${sharpenSeg}`;
        }
      } catch {
        // ignore and fall through
      }
    } else if (variantsJson) {
      try {
        const url = new URL(src);
        const parts = url.pathname.split('/').filter(Boolean);
        // Expect: /<hash>/<id>/<variant>
        if (parts.length >= 3) {
          parts[2] = pickVariant(width); // replace variant
          url.pathname = `/${parts.join('/')}`;
          return url.toString();
        }
      } catch {
        // ignore and fall through
      }
    }
    return src; // leave as-is
  }

  // --- Cloudflare Image Resizing (your host behind CF) ---
  const cfHost = process.env.NEXT_PUBLIC_CLOUDFLARE_IMAGE_HOST;
  if (cfHost) {
    const isAbsolute = /^https?:\/\//i.test(src);
    const normalizedHost = cfHost.endsWith('/') ? cfHost.slice(0, -1) : cfHost;
    const normalizedSrc = isAbsolute ? src : src.startsWith('/') ? src : `/${src}`;
    return `${normalizedHost}/cdn-cgi/image/width=${width},quality=${q},format=auto${isAbsolute ? '/' : ''}${normalizedSrc}`;
  }

  // Fallback: return original src
  return src;
}
